// Configuração da API - Central do Assinante
// Endpoints corrigidos baseados na documentação do backend Delphi
//
// REACT_APP_API_BASE_URL (opcional) sobrescreve tudo (ex.: https://dominio/centraldoassinante/api)
// REACT_APP_LOGIN_MODULO (opcional) — só se o POST /api/login for o mesmo Horse que exige "modulo" (ex. técnicos)
// Em produção, o padrão usa window.location.origin para evitar HTTP em página HTTPS e garantir
// o mesmo host do proxy nginx.

function getApiBaseUrl() {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env && String(env).trim()) {
    return String(env).trim().replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
    return `${window.location.origin}/centraldoassinante/api`;
  }
  return 'http://10.0.201.35:9000/api';
}

/** CPF 11 dígitos → 000.000.000-00 (alguns backends Horse/Delphi gravam assim) */
function formatCpfDigitsToMasked(digits) {
  const d = String(digits).replace(/\D/g, '').slice(0, 11);
  if (d.length !== 11) return '';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** Path começando com / → URL absoluta para fetch e window.open */
function apiUrl(path) {
  const base = getApiBaseUrl();
  const p = path.startsWith('/') ? path : `/${path}`;
  if (base.startsWith('http')) {
    return `${base}${p}`;
  }
  if (typeof window === 'undefined') {
    return `${base}${p}`;
  }
  return `${window.location.origin}${base.startsWith('/') ? base : `/${base}`}${p}`;
}

/** Resposta texto que imita resumo de boleto (o Horse devolve PDF binário; isto indica proxy/rota errada) */
function looksLikePlainTextBoletoSummary(text) {
  const t = (text || '').trim();
  if (t.length < 8) return false;
  if (/^Boleto:\s*\d+/im.test(t)) return true;
  if (/Referência:/i.test(t) && /Valor:\s*R\$/i.test(t)) return true;
  return false;
}

/** Índice do primeiro %PDF (corrige BOM, espaços, aviso antes do binário) */
function findPdfStartOffset(u8) {
  const max = Math.min(u8.length, 262144);
  for (let i = 0; i <= max - 4; i += 1) {
    if (
      u8[i] === 0x25 &&
      u8[i + 1] === 0x50 &&
      u8[i + 2] === 0x44 &&
      u8[i + 3] === 0x46
    ) {
      return i;
    }
  }
  return -1;
}

function tryPdfArrayBufferFromBase64(str) {
  const clean = String(str).replace(/\s/g, '');
  if (clean.length < 80) return null;
  try {
    const binary = atob(clean);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      out[i] = binary.charCodeAt(i);
    }
    const off = findPdfStartOffset(out);
    if (off >= 0) {
      return out.slice(off).buffer;
    }
  } catch {
    return null;
  }
  return null;
}

function tryPdfFromJsonText(payloadText) {
  try {
    const p = JSON.parse(payloadText);
    const keys = [
      'pdfBase64',
      'pdf_base64',
      'base64',
      'arquivoBase64',
      'arquivo',
      'pdf',
      'conteudo',
      'data',
      'file',
    ];
    for (const k of keys) {
      const v = p[k];
      if (typeof v === 'string' && v.length > 40) {
        const buf = tryPdfArrayBufferFromBase64(v);
        if (buf) return buf;
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Chave usada em GET /boletos/:chave/pdf — inclui NÚMERO exibido na grade (ex.: 395485)
 */
export function resolveBoletoChaveFromFatura(fatura) {
  if (!fatura) return '';
  const pick = (...vals) => {
    for (const v of vals) {
      if (v == null || v === '') continue;
      const s = String(v).trim();
      if (s) return s;
    }
    return '';
  };
  const key = pick(
    fatura.chaveBoleto,
    fatura.chave,
    fatura.boletoId,
    fatura.idBoleto,
    fatura.chave_boleto,
    fatura.numero,
    fatura.numeroBoleto,
    fatura.nossoNumero,
    fatura.codigoBoleto,
    fatura.Numero,
    fatura.NumeroBoleto,
    fatura.ChaveBoleto
  );
  if (key) return key;
  const cod = fatura.codigo;
  if (cod != null && String(cod).trim() !== '') {
    const c = String(cod).trim();
    if (/^\d{4,}$/.test(c)) return c;
  }
  return '';
}

// Configuração da API
const apiConfig = {
  get baseURL() {
    return getApiBaseUrl();
  },
  
  // Endpoints de saúde
  health: '/health',
  
  // Endpoints de usuários
  users: {
    getUser: (cpf) => `/users/${cpf}`,
    updateUser: (cpf) => `/users/${cpf}`,
    cadastro: '/cadastro',

    /** POST — autenticação Central do Assinante (corpo típico: { cpf, senha }) */
    login: '/login'
  },
  
  // Endpoints de boletos (CORRIGIDOS)
  boletos: {
    // ✅ CORRETO: Busca boletos pelo ID do cliente
    getBoletosByClienteId: (clienteId) => `/boletos/cliente/${clienteId}`,
    
    // ✅ CORRETO: Busca boletos pelo CPF (POST)
    buscaBoletosByCPF: '/boletos/buscaboletocliente',
    
    // ❌ INCORRETO: Retorna erro de numeric overflow
    getBoletoById: (boletoId) => `/boletos/${boletoId}`,
    
    // ✅ CORRETO: Pagamento de boleto
    payBoleto: (boletoId) => `/boletos/${boletoId}/pay`
  },
  
  // Endpoints de notificações (CORRIGIDOS)
  notifications: {
    // ✅ CORRETO: Busca todas as notificações
    getAll: '/notifications',
    
    // ✅ CORRETO: Marca notificação como lida
    markAsRead: (notificationId) => `/notifications/${notificationId}/read`
  },
  
  // Endpoints de documentos (CORRIGIDOS)
  documents: {
    // ✅ CORRETO: Busca todos os documentos
    getAll: '/documents'
  },

  // Chamados (Atendido) - backend deve retornar DISTINCT nome+bairro e chamados por cliente
  chamados: {
    // Lista de clientes distintos (nome + bairro) que já abriram chamado
    getClientesAtendido: '/chamados/atendido/clientes',
    // Chamados de um cliente, ordenados por data de abertura crescente
    getChamadosByCliente: (clienteId) => `/chamados/cliente/${clienteId}`
  }
};

// Funções auxiliares para a API
const apiHelpers = {
  // Busca ID do cliente pelo CPF
  getClienteIdByCPF: async (cpf) => {
    try {
      const response = await fetch(`${apiConfig.baseURL}/users/${cpf}`);
      const userData = await response.json();
      return userData.id; // Retorna o ID do cliente
    } catch (error) {
      console.error('Erro ao buscar ID do cliente:', error);
      throw error;
    }
  },
  
  // Busca boletos pelo CPF (usando o fluxo correto)
  getBoletosByCPF: async (cpf) => {
    try {
      // 1. Busca o ID do cliente pelo CPF
      const clienteId = await apiHelpers.getClienteIdByCPF(cpf);
      
      // 2. Busca os boletos pelo ID do cliente
      const response = await fetch(`${apiConfig.baseURL}/boletos/cliente/${clienteId}`);
      const boletos = await response.json();
      
      return boletos;
    } catch (error) {
      console.error('Erro ao buscar boletos:', error);
      throw error;
    }
  },
  
  // Busca notificações
  getNotifications: async () => {
    try {
      const response = await fetch(`${apiConfig.baseURL}/notifications`);
      const notifications = await response.json();
      return notifications;
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      throw error;
    }
  },
  
  // Busca documentos
  getDocuments: async () => {
    try {
      const response = await fetch(`${apiConfig.baseURL}/documents`);
      const documents = await response.json();
      return documents;
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      throw error;
    }
  }
};

// API principal com todos os métodos
const api = {
  // Configuração
  config: apiConfig,
  
  // Métodos de saúde
  health: async () => {
    const response = await fetch(`${apiConfig.baseURL}/health`);
    return response.json();
  },
  
  // Métodos de usuários
  getUser: async (cpf) => {
    const response = await fetch(`${apiConfig.baseURL}/users/${cpf}`);
    return response.json();
  },
  
  updateUser: async (cpf, data) => {
    const response = await fetch(`${apiConfig.baseURL}/users/${cpf}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    return response.json();
  },
  
  cadastro: async (data) => {
    const response = await fetch(`${apiConfig.baseURL}/cadastro`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  /**
   * Login Central — POST /api/login (baseURL já inclui /api).
   * Envia cpf (11 dígitos), senha; opcionalmente cpfFormatado e modulo (ver REACT_APP_LOGIN_MODULO).
   */
  loginAssinante: async (cpfRaw, senha) => {
    const cpfDigits = String(cpfRaw ?? '').replace(/\D/g, '');
    if (cpfDigits.length !== 11) {
      throw new Error('Informe um CPF válido (11 dígitos).');
    }
    const url = `${apiConfig.baseURL}${apiConfig.users.login || '/login'}`;
    const cpfFormatado = formatCpfDigitsToMasked(cpfDigits);
    const loginBody = {
      cpf: cpfDigits,
      senha,
    };
    if (cpfFormatado) {
      loginBody.cpfFormatado = cpfFormatado;
    }
    const moduloExtra = process.env.REACT_APP_LOGIN_MODULO;
    if (moduloExtra && String(moduloExtra).trim()) {
      loginBody.modulo = String(moduloExtra).trim();
    }

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(loginBody),
      });
    } catch (e) {
      const msg =
        e && typeof e.message === 'string' ? e.message : 'Falha de rede';
      throw new Error(
        `Não foi possível contactar a API (${msg}). Verifique HTTPS, o proxy /centraldoassinante/api e o backend em 10.0.201.35:9000.`
      );
    }

    const text = await response.text();
    const ct = (response.headers.get('content-type') || '').toLowerCase();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      const preview = (text || '').slice(0, 80).replace(/\s+/g, ' ');
      if (ct.includes('html') || /^\s*</.test(text)) {
        throw new Error(
          'O servidor devolveu HTML em vez de JSON no login. O nginx pode estar a encaminhar POST /api/login para o sítio errado ou a rota não existe.'
        );
      }
      throw new Error(
        preview
          ? `Resposta inválida do servidor: ${preview}…`
          : 'Resposta inválida do servidor (não é JSON).'
      );
    }
    if (!response.ok) {
      const msg =
        data.erro ||
        data.mensagem ||
        data.message ||
        data.error ||
        (response.status === 404
          ? 'Login indisponível (rota não encontrada). Confirme POST /api/login no backend e o proxy nginx.'
          : `Não foi possível entrar (${response.status})`);
      throw new Error(typeof msg === 'string' ? msg : 'Falha no login.');
    }
    return { data, cpfDigits };
  },
  
  // Métodos de boletos (CORRIGIDOS)
  getBoletos: async (clienteId) => {
    const response = await fetch(`${apiConfig.baseURL}/boletos/cliente/${clienteId}`);
    return response.json();
  },
  
  getBoletoById: async (boletoId) => {
    const response = await fetch(`${apiConfig.baseURL}/boletos/${boletoId}`);
    return response.json();
  },
  
  payBoleto: async (boletoId, data) => {
    const response = await fetch(`${apiConfig.baseURL}/boletos/${boletoId}/pay`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    return response.json();
  },

  downloadBoletoPdf: async (boletoChave) => {
    const id = String(boletoChave ?? '').trim();
    if (!id) {
      throw new Error('Chave do boleto não informada');
    }

    const pdfPath = `/boletos/${encodeURIComponent(id)}/pdf`;
    const fetchUrl = apiUrl(pdfPath);

    let sameOrigin = false;
    try {
      sameOrigin = new URL(fetchUrl).origin === window.location.origin;
    } catch {
      sameOrigin = false;
    }

    // #region agent log
    const __agentLog = (message, data, hypothesisId) => {
      fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Session-Id': 'a5257d',
        },
        body: JSON.stringify({
          sessionId: 'a5257d',
          location: 'api-config.js:downloadBoletoPdf',
          message,
          data: { ...data, hypothesisId },
          timestamp: Date.now(),
          hypothesisId,
        }),
      }).catch(() => {});
    };
    __agentLog('boleto_pdf_start', {
      id,
      fetchUrl,
      apiBase: getApiBaseUrl(),
      pageOrigin: typeof window !== 'undefined' ? window.location.origin : '',
      nodeEnv: process.env.NODE_ENV,
      sameOrigin,
    }, 'H1');
    // #endregion

    const triggerBlobDownload = (buf, filename) => {
      const safeName = filename.toLowerCase().endsWith('.pdf')
        ? filename
        : `${filename.replace(/\.[^/.]+$/, '')}.pdf`;
      const pdfBlob = new Blob([buf], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = safeName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    };

    const openPdfInNewTab = () => {
      window.open(fetchUrl, '_blank', 'noopener,noreferrer');
    };

    let response;
    try {
      response = await fetch(fetchUrl, {
        headers: {
          Accept: 'application/pdf,application/octet-stream;q=0.9,*/*;q=0.8',
        },
        credentials: sameOrigin ? 'include' : 'omit',
        mode: 'cors',
      });
    } catch (networkErr) {
      // #region agent log
      fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
        body: JSON.stringify({
          sessionId: 'a5257d',
          location: 'api-config.js:downloadBoletoPdf',
          message: 'fetch_network_error',
          data: { id: String(id), fetchUrl, errName: networkErr?.name },
          timestamp: Date.now(),
          hypothesisId: 'H4',
        }),
      }).catch(() => {});
      // #endregion
      openPdfInNewTab();
      return true;
    }

    // #region agent log
    fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
      body: JSON.stringify({
        sessionId: 'a5257d',
        location: 'api-config.js:downloadBoletoPdf',
        message: 'fetch_response_headers',
        data: {
          id: String(id),
          ok: response.ok,
          status: response.status,
          contentType: response.headers.get('content-type') || '',
          contentDisposition: response.headers.get('content-disposition') || '',
        },
        timestamp: Date.now(),
        hypothesisId: 'H1',
      }),
    }).catch(() => {});
    // #endregion

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      if (response.status === 401 || response.status === 403) {
        openPdfInNewTab();
        return true;
      }
      throw new Error(
        errText
          ? `Erro ao baixar boleto (${response.status}): ${errText.slice(0, 200)}`
          : `Erro ao baixar boleto (HTTP ${response.status})`
      );
    }

    const buf = await response.arrayBuffer();
    const u8 = new Uint8Array(buf);

    const headHex =
      u8.length === 0
        ? ''
        : Array.from(u8.slice(0, Math.min(16, u8.length)))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join(' ');

    const pdfOff = findPdfStartOffset(u8);

    // #region agent log
    fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
      body: JSON.stringify({
        sessionId: 'a5257d',
        location: 'api-config.js:downloadBoletoPdf',
        message: 'body_fingerprint',
        data: {
          id: String(id),
          byteLength: buf.byteLength,
          headHex,
          pdfOff,
          looksLikeAsciiText: u8.length > 0 && u8[0] !== 0x25,
        },
        timestamp: Date.now(),
        hypothesisId: 'H2',
      }),
    }).catch(() => {});
    // #endregion

    if (pdfOff >= 0) {
      const slice = u8.slice(pdfOff).buffer;
      // #region agent log
      fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
        body: JSON.stringify({
          sessionId: 'a5257d',
          location: 'api-config.js:downloadBoletoPdf',
          message: 'branch_pdf_slice_blob',
          data: { id: String(id), pdfOff },
          timestamp: Date.now(),
          hypothesisId: 'H2',
        }),
      }).catch(() => {});
      // #endregion
      triggerBlobDownload(slice, `boleto-${id}.pdf`);
      return true;
    }

    const payloadText = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    const trimmed = payloadText.trim();

    // #region agent log
    fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
      body: JSON.stringify({
        sessionId: 'a5257d',
        location: 'api-config.js:downloadBoletoPdf',
        message: 'text_body_preview',
        data: {
          id: String(id),
          preview: trimmed.slice(0, 120),
          startsWithBoleto: /^Boleto:/i.test(trimmed),
        },
        timestamp: Date.now(),
        hypothesisId: 'H1',
      }),
    }).catch(() => {});
    // #endregion

    if (looksLikePlainTextBoletoSummary(trimmed)) {
      // #region agent log
      fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
        body: JSON.stringify({
          sessionId: 'a5257d',
          location: 'api-config.js:downloadBoletoPdf',
          message: 'reject_text_summary_not_pdf',
          data: { id: String(id), fetchUrl: String(fetchUrl).slice(0, 200) },
          timestamp: Date.now(),
          hypothesisId: 'H1',
        }),
      }).catch(() => {});
      // #endregion
      throw new Error(
        'O servidor devolveu um resumo em texto em vez do PDF. O endpoint HTTPS (nginx/proxy) ' +
          'precisa encaminhar GET /api/boletos/' +
          id +
          '/pdf para o Horse com o mesmo binário que em http://10.0.201.35:9000. ' +
          'Corrija o nginx ou a rota da API até a resposta começar com bytes %PDF.'
      );
    }

    const fromJson = tryPdfFromJsonText(payloadText);
    if (fromJson) {
      triggerBlobDownload(fromJson, `boleto-${id}.pdf`);
      return true;
    }

    const fromB64 = tryPdfArrayBufferFromBase64(trimmed);
    if (fromB64) {
      triggerBlobDownload(fromB64, `boleto-${id}.pdf`);
      return true;
    }

    if (/^<[!\s]/i.test(trimmed) || trimmed.toLowerCase().includes('<html')) {
      openPdfInNewTab();
      return true;
    }

    let candidateUrl = '';
    try {
      const parsed = JSON.parse(payloadText);
      candidateUrl =
        parsed?.pdfUrl ||
        parsed?.url ||
        parsed?.link ||
        parsed?.href ||
        '';
    } catch {
      if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
        candidateUrl = trimmed;
      }
    }

    if (candidateUrl) {
      if (/^https?:\/\//i.test(candidateUrl)) {
        const normalizedUrl = candidateUrl.replace(/\.txt(\?.*)?$/i, '.pdf$1');
        window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
        return true;
      }
      if (candidateUrl.startsWith('/')) {
        window.open(apiUrl(candidateUrl), '_blank', 'noopener,noreferrer');
        return true;
      }
    }

    // #region agent log
    fetch('http://127.0.0.1:7646/ingest/1ef9562e-c9f3-4574-ac7f-00adcd777fdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'a5257d' },
      body: JSON.stringify({
        sessionId: 'a5257d',
        location: 'api-config.js:downloadBoletoPdf',
        message: 'branch_fallback_throw_not_open_tab',
        data: { id: String(id), fetchUrl: String(fetchUrl).slice(0, 200) },
        timestamp: Date.now(),
        hypothesisId: 'H5',
      }),
    }).catch(() => {});
    // #endregion
    throw new Error(
      'Não foi possível obter um PDF válido desta URL. A resposta não contém %PDF nem base64. ' +
        'Verifique o backend e o nginx para GET /boletos/' +
        id +
        '/pdf.'
    );
  },
  
  // Métodos de notificações (CORRIGIDOS)
  getNotifications: async () => {
    const response = await fetch(`${apiConfig.baseURL}/notifications`);
    return response.json();
  },
  
  markNotificationAsRead: async (notificationId) => {
    const response = await fetch(`${apiConfig.baseURL}/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
    return response.json();
  },
  
  // Métodos de documentos (CORRIGIDOS)
  getDocuments: async () => {
    const response = await fetch(`${apiConfig.baseURL}/documents`);
    return response.json();
  },

  // Chamados (Atendido)
  getClientesAtendido: async () => {
    const response = await fetch(`${apiConfig.baseURL}${apiConfig.chamados.getClientesAtendido}`);
    if (!response.ok) throw new Error('Erro ao carregar clientes');
    return response.json();
  },

  getChamadosByCliente: async (clienteId) => {
    const response = await fetch(`${apiConfig.baseURL}${apiConfig.chamados.getChamadosByCliente(clienteId)}`);
    if (!response.ok) throw new Error('Erro ao carregar chamados do cliente');
    return response.json();
  },
  
  // Helpers
  helpers: apiHelpers
};

export default api;
